import { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import Helmet from '@/components/meta/Helmet';
import ErrorTemplate from '@/components/templates/Error';
import RaceTemplate from '@/components/templates/Velodrome/Race/Singlerace';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useUserContext } from '@/contexts/user';
import {
  BetType,
  EntryRacerNote,
  EntryRacerScoreAttByCond,
  EntryRacerScoreDetailOwn,
  GetOddsByConditionQueryVariables,
  GetRaceQueryVariables,
  GetScoreDetailOwnsInputParam,
  OrderSingleRace,
  Race,
  RaceProgram,
  useGetEntryRacerNotesLazyQuery,
  useGetEntryRacerScoreDetailOwnsConditionLazyQuery,
  useGetOddsByConditionQuery,
  useGetOrderSingleRacesByRaceIdLazyQuery,
  useGetRaceProgramsQuery,
  useGetRaceQuery,
  useGetUserWalletLazyQuery,
  useListRacerScoreByAttributeLazyQuery,
  User,
  UserStatus,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { formatProgramName, parseOddsInfo } from '@/lib/race';
import { isCrawler } from '@/lib/userAgent';
import { BetMethod, Odds, OddsData, RaceInfoType } from '@/modelTypes';
import { VelodromeRoutesProps } from '@/routes/Velodrome';
import { assertIsDefined, toHalfWidth } from '@/utility/utils';

const initOddsData = (): OddsData => {
  const odds = {} as Odds;
  Object.values(BetType).forEach(type => {
    odds[type] = {
      detail: [],
      releaseBetCount: 0,
      returnBetCount: 0,
      salesBetCount: 0,
    };
  });
  return {
    exists: false,
    data: odds,
  };
};

const getRaceInfoTypeName = (infoType?: RaceInfoType) => {
  switch (infoType) {
    case RaceInfoType.Basic: {
      return '出走表';
    }
    case RaceInfoType.Result: {
      return '結果';
    }
    case RaceInfoType.Odds: {
      return 'オッズ';
    }
    default: {
      return '出走表';
    }
  }
};

const Singlerace: FC<VelodromeRoutesProps> = ({ velodrome }) => {
  const setBreadcrumbs = useSetBreadcrumbContext();
  const { heldDate, raceNum } = useParams<{
    heldDate?: string;
    raceNum: string;
  }>();
  assertIsDefined(raceNum);

  const location = useLocation();
  const { user, setUser, isMember } = useUserContext();

  const [raceInfoType, setRaceInfoType] = useState<RaceInfoType>(
    RaceInfoType.Basic,
  );
  const [betMethod, setBetMethod] = useState<BetMethod>(BetMethod.Formation);
  const [oddsData, setOddsData] = useState<OddsData>(initOddsData());
  const [oddsUpdateTime, setOddsUpdateTime] = useState<Dayjs>(dayjst());
  const [hasInfoType, setHasInfoType] = useState<boolean>(false);

  const targetDate = useMemo(
    () => (heldDate ? dayjst(heldDate) : null),
    [heldDate],
  );

  const raceProgramsQuery = useGetRaceProgramsQuery({
    variables: {
      input: {
        heldDate: dayjst().startOf('day'),
      },
    },
  });
  const raceQuery = useGetRaceQuery({
    variables: {
      raceInput: {
        velodromeCode: velodrome.code,
        heldDate: targetDate,
        raceNum: Number(raceNum),
      },
      raceProgramInput: {
        velodromeCode: velodrome.code,
        heldDate: targetDate,
      },
    } as GetRaceQueryVariables,
    fetchPolicy: 'network-only',
  });

  const oddsQuery = useGetOddsByConditionQuery({
    variables: {
      raceInput: {
        velodromeCode: velodrome.code,
        heldDate: targetDate,
        raceNum: Number(raceNum),
      },
    } as GetOddsByConditionQueryVariables,
    pollInterval: 30 * 1000,
    fetchPolicy: 'network-only',
  });

  const [loadEntryRacerNotes, entryRacerNotesQuery] =
    useGetEntryRacerNotesLazyQuery({
      fetchPolicy: 'network-only',
    });

  const [loadOrderSingleraces, orderSingleracesQuery] =
    useGetOrderSingleRacesByRaceIdLazyQuery({
      fetchPolicy: 'network-only',
    });

  const [loadEntryRacerScoreDetailOwns, entryRacerScoreDetailOwns] =
    useGetEntryRacerScoreDetailOwnsConditionLazyQuery();

  const [loadRacerScoreByAttributes, racerScoreByAttributes] =
    useListRacerScoreByAttributeLazyQuery();

  const getRacerScoreByAttributes = (argRaceId: string) => {
    if (raceInfoType === RaceInfoType.Line) {
      loadRacerScoreByAttributes({ variables: { raceId: argRaceId } });
    }
  };

  const getScoreDetailOwn = (
    argRaceId: string,
    argHeldDate: string,
    argLimitHeldDate: string,
    argVelodromeCode: number,
  ) => {
    if (argRaceId != '' && argHeldDate != '') {
      var argDetailCnt = Number(import.meta.env.VITE_RACER_SCORE_DETAIL_COUNT);

      if (raceInfoType === RaceInfoType.Basic) {
        argDetailCnt = 24 * 100;
      } else if (raceInfoType === RaceInfoType.VelodromeResult) {
        argDetailCnt = Number(
          import.meta.env.VITE_RACER_SCORE_DETAIL_COUNT_VELODROME,
        );
      } else {
        argDetailCnt = argDetailCnt * 10;
      }

      const variables = {
        input: {
          raceId: argRaceId,
          heldDate: argHeldDate,
          detailCnt: argDetailCnt,
          limitHeldDate: argLimitHeldDate,
          velodromeCode: argVelodromeCode,
        } as GetScoreDetailOwnsInputParam,
      };

      loadEntryRacerScoreDetailOwns({ variables });
    }
  };

  useEffect(() => {
    if (location.search) {
      const infoType =
        new URLSearchParams(location.search).get('infoType') || '';
      if ((Object.values(RaceInfoType) as string[]).includes(infoType)) {
        setRaceInfoType(infoType as RaceInfoType);
        setHasInfoType(true);
      }
    } else {
      setRaceInfoType(RaceInfoType.Basic);
      setHasInfoType(false);
    }
  }, [location.search]);

  useEffect(() => {
    if (!oddsQuery.error && oddsQuery.data && oddsQuery.data.odds) {
      const oddsUpdateTimeData: Dayjs = oddsQuery.data.odds.updated || dayjst();
      setOddsData({
        exists: oddsQuery.data.odds.oddsInfo != '',
        data: parseOddsInfo(oddsQuery.data.odds.oddsInfo),
      });
      setOddsUpdateTime(oddsUpdateTimeData);
    } else {
      setOddsData(initOddsData());
    }
  }, [oddsQuery.data, oddsQuery.loading]);

  useEffect(() => {
    if (raceQuery.data && !raceQuery.loading) {
      if (isMember) {
        loadEntryRacerNotes({
          variables: {
            raceId: raceQuery.data.race.id,
          },
        });
        loadOrderSingleraces({
          variables: {
            raceId: raceQuery.data.race.id,
          },
        });
      }
      setBreadcrumbs([
        {
          name: `${velodrome.name}競輪`,
          to: `/${velodrome.romanName}`,
        },
        {
          name: `${formatProgramName(
            raceQuery.data.raceProgram as RaceProgram,
          )} （${raceQuery.data.raceProgram.heldDate.format('YYYY年M月D日')}）`,
          to: `/${
            velodrome.romanName
          }/racecard/${raceQuery.data.raceProgram.heldDate.format(
            'YYYY-MM-DD',
          )}`,
        },
        {
          name: `${raceQuery.data.race.raceNum}R`,
          to: '',
        },
      ]);
    }
  }, [raceQuery.data, raceQuery.loading]);

  const [scoreDetailOwns, setScoreDetailOwns] =
    useState<EntryRacerScoreDetailOwn[]>();
  const [scoreDetailMatchByBasic, setScoreDetailMatchByBasic] =
    useState<EntryRacerScoreDetailOwn[]>();
  const [scoreDetailMatchByLine, setScoreDetailMatchByLine] =
    useState<EntryRacerScoreDetailOwn[]>();
  const [scoreByAttribute, setScoreByAttribute] =
    useState<EntryRacerScoreAttByCond[]>();

  useEffect(() => {
    if (entryRacerScoreDetailOwns.data) {
      var results: Array<EntryRacerScoreDetailOwn> = [];
      entryRacerScoreDetailOwns.data.entryRacerScoreDetailOwnsCondition.forEach(
        (own: any) => {
          results.push(own as EntryRacerScoreDetailOwn);
        },
      );

      if (raceInfoType === RaceInfoType.Basic) {
        setScoreDetailMatchByBasic(results);
      } else if (raceInfoType === RaceInfoType.Line) {
        setScoreDetailMatchByLine(results);
      } else {
        setScoreDetailOwns(results);
      }
    }
  }, [entryRacerScoreDetailOwns.data, entryRacerScoreDetailOwns.loading]);

  useEffect(() => {
    if (racerScoreByAttributes.data) {
      var results: Array<EntryRacerScoreAttByCond> = [];
      racerScoreByAttributes.data.listRacerScoreByAttribute.forEach(
        (own: any) => {
          results.push(own as EntryRacerScoreAttByCond);
        },
      );

      setScoreByAttribute(results);
    }
  }, [racerScoreByAttributes.data, racerScoreByAttributes.loading]);

  // ウォレット取得
  const [walletLazyQuery, walletLazyQueryResult] = useGetUserWalletLazyQuery({
    errorPolicy: 'ignore',
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    if (user.status === UserStatus.Regular && !walletLazyQueryResult.called) {
      walletLazyQuery();
    }
  }, []);
  useEffect(() => {
    if (
      walletLazyQueryResult.called &&
      !walletLazyQueryResult.loading &&
      walletLazyQueryResult.data
    ) {
      setUser(walletLazyQueryResult.data.user as User);
    }
  }, [walletLazyQueryResult.called, walletLazyQueryResult.data]);

  const canonical =
    raceInfoType === RaceInfoType.Result
      ? `/${
          velodrome.romanName
        }/race/${raceQuery.data?.raceProgram.heldDate.format(
          'YYYY-MM-DD',
        )}/result`
      : `/${
          velodrome.romanName
        }/racecard/${raceQuery.data?.raceProgram.heldDate.format(
          'YYYY-MM-DD',
        )}`;

  const title = `${velodrome.name}競輪 ${toHalfWidth(
    raceQuery.data ? raceQuery.data.raceProgram.programName : '',
  )}（${dayjst(heldDate).format(
    'YYYY年M月D日',
  )}) ${raceNum}レース ${getRaceInfoTypeName(raceInfoType)}`;

  const description = `${velodrome.name}競輪 ${toHalfWidth(
    raceQuery.data ? raceQuery.data.raceProgram.programName : '',
  )}の${getRaceInfoTypeName(raceInfoType)}ページ。`;

  return (
    <>
      <Helmet
        meta={{
          title,
          description: `${description}%s`,
          keywords: `${title},%s`,
          robots: heldDate ? 'noindex' : '',
        }}
        canonical={isCrawler ? canonical : undefined}
      />
      {raceQuery.error || raceProgramsQuery.error ? (
        <ErrorTemplate
          apolloError={raceQuery.error || raceProgramsQuery.error}
        />
      ) : (
        <>
          {raceQuery.data && (
            <RaceTemplate
              race={raceQuery.data.race as Race}
              raceProgram={raceQuery.data.raceProgram as RaceProgram}
              racePrograms={
                (raceProgramsQuery.data?.racePrograms || []) as RaceProgram[]
              }
              oddsData={oddsData}
              oddsQueryRefetch={() => oddsQuery.refetch()}
              oddsUpdateTime={oddsUpdateTime}
              raceInfoType={raceInfoType}
              setRaceInfoType={setRaceInfoType}
              hasInfoType={hasInfoType}
              betMethod={betMethod}
              setBetMethod={setBetMethod}
              walletQueryRefetch={walletLazyQueryResult.refetch}
              raceQueryRefetch={() => raceQuery.refetch()}
              entryRacerNotes={
                entryRacerNotesQuery.data?.entryRacerNotes as EntryRacerNote[]
              }
              orderSingleraces={
                (orderSingleracesQuery.data
                  ? orderSingleracesQuery.data.orderSingleRaces
                  : []) as OrderSingleRace[]
              }
              orderSingleracesRefetch={() =>
                loadOrderSingleraces({
                  variables: {
                    raceId: raceQuery.data!.race.id,
                  },
                })
              }
              getScoreDetailOwn={getScoreDetailOwn}
              getRacerScoreByAttributes={getRacerScoreByAttributes}
              scoreDetailOwns={scoreDetailOwns}
              scoreDetailMatchByBasic={scoreDetailMatchByBasic}
              scoreDetailMatchByLine={scoreDetailMatchByLine}
              scoreByAttribute={scoreByAttribute}
            />
          )}
        </>
      )}
    </>
  );
};
export default Singlerace;
